import { useContext, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getApplication, getUser } from "../Services/applicationService.jsx";
import { UnauthorizedError, ForbiddenError } from "../Utils/Errors.tsx";
import { AppContext } from "../contexts/AppContext.jsx";
import Loader from "./Loader/Loader.jsx";
import {
  getCountryByLabel,
  getValueFromLabel,
} from "../Services/localStorage.tsx";
import loginEnvMap from "../config.js";
import { toTitleCase } from "../Utils/stringHandler.tsx"; // Assuming you have a utility function for title case

const TokenGate = ({ onValidated }) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setAppState, userFlows, setUserFlows, setUserRegister, isInitialized, setIsInitialized } =
    useContext(AppContext);
  const [relayStateLocal, setRelayStateLocal] = useState("");

  const queryParams = new URLSearchParams(search);
  const flow = queryParams.get("flow") || "";
  const relayState = queryParams.get("relayState") || "";

  useEffect(() => {
    if (relayState) {
      localStorage.setItem("relayState", relayState);
    }
    if (localStorage.getItem("relayState")?.includes("manage")) {
      console.log("Relay state indicates manage flow");
      setRelayStateLocal(relayState);
    }
  }, [queryParams]);

  const extractGender = async (gender) => {
    const genderId = await getValueFromLabel("GenderReference", gender);
    return genderId;
  };

  const initializeApp = useCallback(async () => {
    try {
      const user = await getUser();

      if (user?.disableLiveCheck) {
        setUserFlows((prev) => ({
          ...prev,
          showLiveness: false,
        }));
      }

      if (user?.contactDetails) {
        setAppState((prev) => ({
          ...prev,
          requestPayload: {
            ...prev.requestPayload,
            applicant: {
              ...prev.requestPayload?.applicant,
              emailAddress: user?.contactDetails?.emailAddress || "",
              mobilePhone: user?.contactDetails?.mobileNumber || "",
            },
          },
        }));
      }

      if (user.applicationId) {
        const app = await getApplication(user.applicationId);
        const details = JSON.parse(app.details.json || "{}");

        setAppState((prev) => ({
          ...prev,
          requestPayload: {
            ...details,
            applicant: {
              ...details.applicant,
              emailAddress: user?.contactDetails?.emailAddress || "",
              mobilePhone: user?.contactDetails?.mobileNumber || "",
            },
            applicationId: user.applicationId,
          },
        }));

        // console.log("Application details fetched:", details?.eventTypeSearch);

        const exists = details?.eventTypeSearch?.some(
          (e) =>
            e.eventTypeId === "700a9fae-a634-ed11-9db1-0022489412bc" ||
            e.eventTypeId === "b9294e90-bcd5-ee11-904c-000d3acb9d09",
        );

        const applicationFlowCode = details?.applicationFlowCode || 0;

        if (exists) {
          setUserFlows((prev) => ({
            ...prev,
            showRefree:
              applicationFlowCode === 100000001 ? false : true,
          }));
        }


        if (applicationFlowCode === 100000005) {
          setUserFlows((prev) => ({
            ...prev,
            isCancelRequested: true,
            isChangeRequested: true,
            dateOfExpiry: user?.register?.verificationDate?.expiryDate || "",
          }));
          navigate("/app/my-identity");
          onValidated?.();
          return;
        }

        if (
          applicationFlowCode === 100000002 ||
          applicationFlowCode === 100000004
        ) {
          setUserFlows((prev) => ({
            ...prev,
            isCancelRequested: true,
            isChangeRequested: true,
            dateOfExpiry: user?.register?.verificationDate?.expiryDate || "",
          }));
          navigate("/app/my-identity");
          onValidated?.();
          return;
        }

        if (applicationFlowCode === 100000001) {
          setUserFlows((prev) => ({
            ...prev,
            isRenew: true,
            showRefree: false,
          }));
        }

        // console.log("User flows updated:", userFlows);
        // console.log("flow:", flow);

        // Handle flow redirects
        switch (flow) {
          case "idcheck_success":
            navigate("/app/post-live-check");
            onValidated?.();
            return;
          case "idcheck_fail":
            navigate("/app/no-photo-verification");
            onValidated?.();
            return;
          case "idcheck_unsuccessful_no_retry":
            navigate("/app/photo-verification-failed-no-retry");
            onValidated?.();
            return;
          case "idcheck_unsuccessful":
            if (user?.disableLiveCheck)
              navigate("/app/photo-verification-failed-no-retry");
            else navigate("/app/photo-verification-failed");
            onValidated?.();
            return;
          default:
            navigate("/app/saved-application");
            onValidated?.();
            return;
        }
      } else {
        // No applicationId - handle based on relayState
        if (user?.register) {
          setUserRegister(user?.register);
          const genderId = await extractGender(user?.register?.gender || "");
          const country = await getCountryByLabel(
            user?.register?.countryOfBirth || "New Zealand",
          );
          console.log("Country:", country);
          const countryId = country?.dia_countryid || "";
          console.log("Country ID:", countryId);
          console.log(user?.register?.verificationDate?.expiryDate || "");
          setUserFlows((prev) => ({
            ...prev,
            dateOfExpiry: user?.register?.verificationDate?.expiryDate || "",
          }));

          setAppState((prev) => ({
            ...prev,
            requestPayload: {
              applicant: {
                givenNames: (
                  user?.register?.personName?.givenNames +
                  " " +
                  (user?.register?.personName?.middleNames || "")
                ).trim(),
                familyName: user?.register?.personName?.familyName,
                dateOfBirth: user?.register?.dateOfBirth || "",
                placeOfBirth: toTitleCase(user?.register?.placeOfBirth || ""),
                countryOfBirthId: countryId,
                genderId: genderId,
                emailAddress: user?.contactDetails?.emailAddress || "",
                landlinePhone: "",
                mobilePhone: user?.contactDetails?.mobileNumber || "",
                workPhone: "",
                namesKnownAs: [
                  {
                    nameTypeId: "3ecbc107-c3b8-e911-a866-000d3ae10c61",
                    givenNames: (
                      user?.register?.personName?.givenNames +
                      " " +
                      (user?.register?.personName?.middleNames || "")
                    ).trim(),
                    familyName: user?.register?.personName?.familyName,
                  },
                ],
              },
              eventTypeSearch: [
                {
                  eventTypeId: "6e17a9e2-ccef-ef11-be1f-7c1e528a0d00",
                  documentNumber: user?.register?.registerKey || "",
                  givenNames: (
                    user?.register?.personName?.givenNames +
                    " " +
                    (user?.register?.personName?.middleNames || "")
                  ).trim(),
                  familyName: user?.register?.personName?.familyName,
                  dateOfBirth: user?.register?.dateOfBirth || "",
                  genderId: genderId,
                  countryOfBirthId: countryId,
                },
              ],
            },
          }));
        }
        console.log("Relay State:", relayStateLocal);
        console.log("User Register:", user.register);
        console.log(localStorage.getItem("relayState")?.includes("manage"));
        if (
          localStorage.getItem("relayState")?.includes("manage") &&
          user.register === null
        ) {
          console.log(
            "Relay state indicates manage flow but no register found, redirecting to /app/something-went-wrong-upgrade",
          );
          navigate("/app/something-went-wrong-upgrade");
          onValidated?.();
          return;
        }
        if (
          user?.register?.identityStatus === "RENEW" ||
          user?.register?.identityStatus === "EXPIRED"
        ) {
          console.log(
            "Relay state indicates manage flow, redirecting to /app/renew-identity",
          );
          navigate("/app/renew-identity");
          onValidated?.();
          return;
        } else if (user?.register?.identityStatus === "VER") {
          console.log(
            "Relay state indicates manage flow, redirecting to /app/verify-identity",
          );
          navigate("/app/my-identity");
          onValidated?.();
          return;
        } else {
          navigate("/app/apply");
          onValidated?.();
          return;
        }
      }
    } catch (err) {
      if (err instanceof ForbiddenError) {
        const apiUrl = process.env.REACT_APP_API_URL;
        const loginUrl = apiUrl ? `${apiUrl}/saml/login` : "/api/saml/login";
        const env = (window.env || "").toLowerCase();
        window.location.href = loginEnvMap[env] || loginUrl;
      } else if (!(err instanceof UnauthorizedError)) {
        console.error("Unexpected error:", err);
        navigate("/app/something-went-wrong");
      }
    }
  }, [navigate, setAppState, flow, onValidated, setUserFlows]);

  useEffect(() => {
    if (isInitialized) {
      onValidated?.();
      setLoading(false);
      return;
    }

    let isMounted = true;

    (async () => {
      await initializeApp();
      if (isMounted) {
        setIsInitialized(true);
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div style={{ color: "red" }}>{error}</div>;
  }

  return null;
};

export default TokenGate;
